import template from "./report-details.html";
import "./report-details.less";
import {
	cloneDeep,
	every,
	filter,
	find,
	forEach,
	forOwn,
	groupBy,
	map,
	maxBy,
	orderBy,
	remove,
	size,
	some
} from "lodash";
import {
	FIELD_KEYS,
	FIELD_TEMPLATES,
	EVENT_DETAIL_TOGGLE,
	ACTION_COLLAPSE_DETAILS,
	ACTION_EXPAND_DETAILS
} from "constants.js";
import { pluralize } from "humanize-plus";
import { getSeasonsByEffectiveRanges } from "../../utils/getSeasonsByEffectiveRanges";
import {
	isFieldValueValid, isValueNullish
} from "../../utils";

export function controller($scope, $logger, appState, fieldValueHelper) {
	const ctrl = this;

	ctrl.$onInit = onInit;
	ctrl.getErrMessage = getErrMessage;
	ctrl.handleDetailDelete = handleDetailDelete;
	ctrl.toggleDeleted = toggleDeleted;
	ctrl.handleDetailRestore = handleDetailRestore;
	ctrl.getTableDataSuffix = getTableDataSuffix;
	ctrl.getTableDataPrefix = getTableDataPrefix;
	ctrl.isTableEmpty = isTableEmpty;
	ctrl.toggleDetail = toggleDetail;
	ctrl.toggleDetailsExpansion = toggleDetailsExpansion;
	ctrl.expandAdditionalDetails = false;
	ctrl.scope = $scope;
	ctrl.setValidations = setValidations;
	ctrl.onClickDetail = onClickDetail;

	ctrl.details = [];
	ctrl.selectedDetail = null;

	$scope.$watch(
		() => ctrl.detailValues,
		(next) => {
			if(!!next) {
				setDetails(next);
			}
		}
	);

	function onInit() {
		ctrl.eventBus = ctrl.reportScope.events;
		ctrl.validationService = ctrl.reportScope.validationService;
		ctrl.permissions = appState.permissions.Reporting;

		ctrl.setCallbacks({
			setValidations: validations => setValidations(validations)
		});

		if(ctrl.registerValueProvider) {
			ctrl.registerValueProvider({
				scope: $scope,
				getValues: (api = false) => {
					const fieldsById = groupBy(ctrl.form.detailFields, f => f.Id);
					const details = getDetailFields();
					const allDetailVals = [];

					forEach(details, detail => {
						forEach(detail.values, v => {
							if(v.DetailId === 0) {
								v.DetailId = detail.detailId;
							}

							if(v.FieldId > 0
								&& detail.isDeleted !== true) {
								let hydratedValue = v;

								if(api === false) {
									hydratedValue = hydrateValue(v, fieldsById);
								}
								allDetailVals.push(hydratedValue);
							}
						});
					});

					return allDetailVals;
				}
			});
		}

		ctrl.eventBus.register(
			ACTION_EXPAND_DETAILS,
			() => {
				toggleDetailsExpansion(true);
			},
			$scope
		);

		ctrl.eventBus.register(
			ACTION_COLLAPSE_DETAILS,
			() => {
				toggleDetailsExpansion(false);
			},
			$scope
		);
	}

	function hydrateValue(value, fields) {
		const applicableField = fields[value.FieldId]?.[0];
		if(value.IsNotAvailable === true) {
			value.Classifications = [];
			value._code = value.Code;
			value.Identifiers = [];
			value.Seasons = [];

			return value;
		}

		if(value.String !== undefined) {
			value.Classifications = [];
			value._code = "";
			value.Identifiers = [];
			value.Seasons = [];

			return value;
		}

		if(!!applicableField.EntityQueryConfiguration && !isValueNullish(value.EntityId)) {
			if(applicableField.EntityQueryConfiguration.RelationshipType.Code === "SELF") {
				const hydratedValue = find(applicableField.Data, d => d.Id === value.EntityId);

				value.Classifications = hydratedValue.Classifications;
				value._code = hydratedValue.Code;
				value.Identifiers = (hydratedValue.Identifiers || []);
				value.Seasons = getSeasons(hydratedValue);
			}
			else {
				let hydratedValue = undefined;
				forEach(applicableField.Data, d => {
					forEach(d.Entities, e => {
						if(e.Entity.Id === value.EntityId) {
							hydratedValue = { primary: e.Entity, friendly: d };
							return false;
						}
					});
					if(!!hydratedValue) {
						return false;
					}
				});

				value.Classifications = !hydratedValue ? [] : [
					...(hydratedValue.friendly && hydratedValue.friendly.Classifications ? hydratedValue.friendly.Classifications : []),
					...(hydratedValue.friendly && hydratedValue.friendly.Classification ? [hydratedValue.friendly.Classification] : []),
					...(hydratedValue.primary && hydratedValue.primary.Classifications ? hydratedValue.primary.Classifications : []),
					...(hydratedValue.primary && hydratedValue.primary.Classification ? [hydratedValue.primary.Classification] : [])
				];
				value._code = !hydratedValue ? null : hydratedValue.primary.Code;
				value.Identifiers = !hydratedValue ? [] : (hydratedValue.friendly.Identifiers || []).concat(hydratedValue.primary.Identifiers || []);
				value.Seasons = !hydratedValue ? [] : getSeasons(hydratedValue.primary);
			}
		}
		else if(!isValueNullish(value.DataId)) {
			const hydratedValue = find(applicableField.Data, d => d.Id === value.DataId);

			value.Classifications = hydratedValue?.Classifications ?? [];
			value._code = hydratedValue?.Code;
		}

		return value;

		function getSeasons(entity) {
			if(entity.Effective?.length > 0) {
				return getSeasonsByEffectiveRanges(entity.Effective, ctrl.form.Seasons);
			}
			return ctrl.form.Seasons;
		}
	}

	function setValidations(validations) {
		if(validations) {
			const errors = validations
				.filter(v => v.code !== "WARNING")
				.map(v => v.rules)
				.reduce((a, b) => a.concat(b), []);
			const warnings = validations
				.filter(v => v.code === "WARNING")
				.map(v => v.rules)
				.reduce((a, b) => a.concat(b), []);

			const detailErrors = errors.filter(err => err.validationScope.Code === "DETAIL");
			const detailWarnings = warnings.filter(warn => warn.validationScope.Code === "DETAIL");

			ctrl.hasErrors = groupBy(detailErrors, e => e.DetailId);
			ctrl.hasWarnings = groupBy(detailWarnings, e => e.DetailId);
		}
	}

	function getErrMessage(str, detail) {
		return `${detail.length} ${pluralize(detail.length, str)}`;
	}

	function toggleDetail(detail) {
		detail.isExpanded = !detail.isExpanded;
		if(!detail.isExpanded) {
			ctrl.expandAdditionalDetails = false;
		} else if(every(ctrl.detailsTable.details, { isExpanded: true })) {
			ctrl.expandAdditionalDetails = true;
		}
	}

	function toggleDetailsExpansion(expandOverride) {
		ctrl.expandAdditionalDetails = expandOverride || !ctrl.expandAdditionalDetails;

		forEach(ctrl.detailsTable.details, detail => {
			detail.isExpanded = ctrl.expandAdditionalDetails;
		});
	}

	function setDetails(detailVals) {
		ctrl.details = cloneDeep(detailVals);
		loadDetailClassifications();

		prepareDetailsTable(ctrl.details);
	}

	function loadDetailClassifications() {
		const selectionFields = ctrl.form.detailFields.filter(field => {
			return field.Template === FIELD_TEMPLATES.Selection || field.Template === FIELD_TEMPLATES.EntitySelector;
		});

		forEach(selectionFields, detailField => {
			let detailFieldValues = [];

			forEach(ctrl.details, detail => {
				const detailRowVals = filter(detail.values, v => v.FieldId === detailField.Id && v.String === undefined);
				if(!!detailRowVals && detailRowVals.length > 0) {
					detailFieldValues = detailFieldValues.concat(detailRowVals);
				}
			});

			forEach(detailFieldValues, detailFieldValue => {
				let itemValue = find(detailField.Data, item => {
					return item.Id === detailFieldValue.DataId || item.Id === detailFieldValue.EntityId || findPrimaryEntity(item, detailFieldValue.EntityId) !== null;
				});

				if(itemValue) {
					if(itemValue.Id !== detailFieldValue.DataId && itemValue.Id !== detailFieldValue.EntityId) {
						itemValue = findPrimaryEntity(itemValue, detailFieldValue.EntityId);
					}

					if(itemValue.Classifications) {
						detailFieldValue.Classifications = itemValue.Classifications;
					}
				}
			});
		});
	}

	function findPrimaryEntity(friendlyEntity, primaryId) {
		if(!friendlyEntity.Entities || friendlyEntity.Entities.length < 1 || every(friendlyEntity.Entities, primary => !primary.Entity || !primary.Entity.Id)) {
			return null;
		}

		for(let i = 0; i < friendlyEntity.Entities.length; i++) {
			const primary = friendlyEntity.Entities[i];
			if(primary.Entity.Id === primaryId) {
				return primary.Entity;
			}
		}

		return null;
	}

	function getDetailFields() {
		return ctrl.detailValues;
	}

	function getTableDataPrefix(data) {
		if(data.unit === "$") {
			return "$";
		}

		return "";
	}

	function getTableDataSuffix(data) {
		if(!!data.unit && data.unit !== "$") {
			return data.unit;
		}

		return "";
	}

	function calculateQuantityPriceProduct(fieldValueDictionary) {
		const fieldValues = Object.values(fieldValueDictionary);

		const hasValidValues = !fieldValues.some(v => v?.Number === undefined);

		if(!hasValidValues) {
			return undefined;
		}

		const product = fieldValueDictionary[FIELD_KEYS.quantity].Number * fieldValueDictionary[FIELD_KEYS.price].Number;

		return product;
	}

	function calcluateOysterTotal(fieldValueDictionary) {
		const fieldValues = Object.values(fieldValueDictionary);

		const hasValidValues = !fieldValues.some(v => v?.Number === undefined);

		if(!hasValidValues) {
			return undefined;
		}

		const quantity = fieldValueDictionary[FIELD_KEYS.quantity].Number;
		const price = fieldValueDictionary[FIELD_KEYS.price].Number;
		const tagBegin = fieldValueDictionary[FIELD_KEYS.tagBegin].Number;
		const tagEnd = fieldValueDictionary[FIELD_KEYS.tagEnd].Number;

		const total = ((tagEnd - tagBegin) + 1) * quantity * price;

		return total;
	}

	//temporary until API supports calculated fields
	function appendCalculatedFields(fields, tableDetails) {
		fields = fields || filter(ctrl.form.detailFields, {
			IsDetailColumnHeader: true
		});
		const calculationFields = fields.reduce((acc, f) => {
			if(f.Key.Code === FIELD_KEYS.quantity || f.Key.Code === FIELD_KEYS.price || f.Key.Code === FIELD_KEYS.tagBegin || f.Key.Code === FIELD_KEYS.tagEnd) {
				acc[f.Key.Code] = f;
			}

			return acc;
		}, {});

		let calculator;

		if(Object.values(calculationFields).length === 0) {
			return;
		}

		if(calculationFields[FIELD_KEYS.quantity] !== undefined && calculationFields[FIELD_KEYS.price] !== undefined) {
			if(calculationFields[FIELD_KEYS.tagBegin] !== undefined && calculationFields[FIELD_KEYS.tagEnd] !== undefined) {
				calculator = calcluateOysterTotal;
			}
			else {
				calculator = calculateQuantityPriceProduct;
			}
		}
		else {
			return;
		}

		const order = maxBy(fields, f => f.Order)?.Order + 1;
		const totalField = {
			Id: -1,
			Name: "Total",
			Order: order,
			Template: "Number",
			RoundingPoint: 2,
			values: [],
			Data: [{ Id: -1, Code: "USD", Name: "USD", Abbreviation: "$" }]
		};

		ctrl.totalSum = 0;
		forOwn(tableDetails, (detailVals, detailId) => {
			const detailValue = {
				DetailId: parseInt(detailId),
				FieldId: -1,
				DataId: -1
			};

			const fieldValues = Object.values(calculationFields).reduce((acc, next) => {
				const nextVal = find(detailVals?.values, v => v.FieldId === next.Id);
				acc[next.Key.Code] = nextVal;

				return acc;
			}, {});

			detailValue.Number = calculator(fieldValues);

			remove(detailVals.values, v => v.FieldId === -1);
			if(Array.isArray(detailVals.values)) {
				detailVals.values.push(detailValue);
			}

			const isDetailDeleted = detailVals.isDeleted;

			if(!isDetailDeleted && !isNaN(detailValue.Number)) {
				ctrl.totalSum += detailValue.Number;
			}

			ctrl.formattedTotalSum = isNaN(ctrl.totalSum) ? "--" : `${ctrl.totalSum.toLocaleString("en", { style: "currency", currency: "USD" })}`;
		});

		return totalField;
	}

	function prepareDetailsTable(details = {}) {
		const detailsArray = Object.values(details);
		const columnFields = ctrl.form.detailFields.filter(f => {
			return f.IsDetailColumnHeader === true && detailsArray.some(d => {
				const matchingVals = filter(d.values, v => v.FieldId === f.Id);
				return some(matchingVals, v => isFieldValueValid(v, f));
			});
		});

		if(columnFields.length === 0) {
			columnFields[0] = ctrl.form.detailFields[0];
		}

		const fieldDictionary = {};
		const totalField = appendCalculatedFields(columnFields, details);
		if(totalField) {
			columnFields.push(totalField);
			fieldDictionary["-1"] = totalField;
		}

		const additionalFields = filter(ctrl.form.detailFields, d => !d.IsDetailColumnHeader);
		separateDetails(details, columnFields, additionalFields);

		const table = [];
		const units = {};

		forEach(ctrl.form.detailFields, field => {
			fieldDictionary[field.Id] = field;
			if(field.Template === FIELD_TEMPLATES.Number || field.Template === FIELD_TEMPLATES.Location) {
				units[field.Id] = {};
				forEach(field.Data, unit => {
					units[field.Id][unit.Id] = unit.Abbreviation;
				});
			}
		});

		units["-1"] = { "-1" : "$" };

		//Loop through each property of groupBy result
		forEach(details, detail => {
			//Create detail with array of field values
			const detailColumnVals = detail.mainVals;
			const detailId = detail.detailId;

			table.push({
				additionalDetails:
					detail.extraVals && size(detail.extraVals) > 0 &&
					Object.values(groupBy(detail.extraVals, "FieldId"))
						.map(extraDetailVals => {
							const extraDetailVal = extraDetailVals[0];
							const additionalField = fieldDictionary[extraDetailVal.FieldId];

							if(additionalField.IsNotReported === true || additionalField.IsHidden === true) {
								return { field: undefined };
							}

							const detailValue = {
								field: additionalField.Name,
								value: fieldValueHelper.formatFieldValue(additionalField, extraDetailVals),
								unit: (!!extraDetailVal && units[extraDetailVal.FieldId]) ? units[extraDetailVal.FieldId][extraDetailVal.DataId ?? extraDetailVal.EntityId] : undefined
							};

							if(detailValue.value && detailValue.value.length > 8) {
								detailValue.tooltipValue = `${detailValue.value}`;
							}

							return detailValue;
						}),
				detailId,
				isDeleted: detail.isDeleted,
				tableValues:
					detailColumnVals &&
					orderBy(
						map(columnFields, field => {
							const ds = filter(detail.values, v => v.FieldId === field.Id);
							const d = ds[0];
							const isNumber = some(ds, v => "Number" in (v || {}));
							return {
								value: ds.length > 0 ? fieldValueHelper.formatFieldValue(field, ds) : fieldValueHelper.formatFieldValue(field, undefined),
								order: field.Order,
								unit: isNumber && d ? units[d.FieldId][d.DataId ?? d.EntityId] : undefined
							};
						}),
						["order"]
					),
				isEmpty: !detailColumnVals || detailColumnVals.length === 0,
				columnQuantity: columnFields.length
			});
		});

		if(some(table, row => !!row.isEmpty && !row.isDeleted)) {
			ctrl.enableAddDetail && ctrl.enableAddDetail(false);
		} else {
			ctrl.enableAddDetail && ctrl.enableAddDetail(true);
		}

		ctrl.allDetails = table;

		ctrl.detailsTable = {
			columns: columnFields.map(f => ({ name: f.Name, code: f.Code })),
			hasDeleted: some(ctrl.detailValues, { isDeleted: true }),
			hasAdditionalDetails: some(table, "additionalDetails")
		};

		showOrHideDeleted();
	}

	function separateDetails(details, columnFields, additionalFields) {
		const columnFieldIds = columnFields.map(f => f.Id);
		const additionalFieldIds = additionalFields.map(f => f.Id);

		forEach(details, detail => {
			detail.mainVals = [];
			detail.extraVals = [];

			forEach(detail.values, val => {
				if(columnFieldIds.indexOf(val.FieldId) > -1) {
					detail.mainVals.push(val);
				}
				else if(additionalFieldIds.indexOf(val.FieldId) > -1) {
					detail.extraVals.push(val);
				}
			});
		});
	}

	function toggleDeleted() {
		ctrl.showDeleted = !ctrl.showDeleted;
		showOrHideDeleted();
	}

	function showOrHideDeleted() {
		ctrl.detailsTable.details = ctrl.allDetails.filter(d => !d.isDeleted || ctrl.showDeleted);
	}

	function handleDetailDelete(detail) {
		ctrl.detailValues[detail.detailId].isDeleted = true;
		detail.isDeleted = true;
		forEach(ctrl.detailValues[detail.detailId].values, v => {
			v.IsDeleted = true;
		});

		ctrl.detailsTable.hasDeleted = some(ctrl.detailValues, { isDeleted: true });
		appendCalculatedFields(undefined, ctrl.detailValues);

		ctrl.eventBus.trigger(EVENT_DETAIL_TOGGLE, { detailId: detail.detailId });
		$logger.event($logger.reportDetailEventBuilder("deleted"), { detailId: detail.detailId });
	}

	function handleDetailRestore(detail) {
		ctrl.detailValues[detail.detailId].isDeleted = false;
		detail.isDeleted = false;
		forEach(ctrl.detailValues[detail.detailId].values, v => {
			v.IsDeleted = false;
		});

		ctrl.detailsTable.hasDeleted = some(ctrl.detailValues, { isDeleted: true });
		appendCalculatedFields(undefined, ctrl.detailValues);

		ctrl.eventBus.trigger(EVENT_DETAIL_TOGGLE, { detailId: detail.detailId });
		$logger.event($logger.reportDetailEventBuilder("restored"), { detailId: detail.detailId });
	}

	function isTableEmpty() {
		if(!ctrl.detailsTable) {
			return true;
		}

		const detailsQty = ctrl.detailsTable.details.filter(detail => !detail.isDeleted || ctrl.showDeleted).length;
		return detailsQty === 0;
	}

	function onClickDetail(detail) {
		const detailWithErrors = {
			...detail
		};

		detailWithErrors.errors = ctrl.hasErrors && ctrl.hasErrors[detail.detailId];
		detailWithErrors.warnings = ctrl.hasWarnings && ctrl.hasWarnings[detail.detailId];

		ctrl.onDetailSelect({ detail: detailWithErrors });
	}

	ctrl.handleKeypressAsButtonClick = (event) => {
		if(event.key === "Enter" || event.key === " ") {
			ctrl.editDetail();
		}
	};
}

export default angular.module("vesl.components.report-details", []).component("reportDetails", {
	template,
	controller,
	bindings: {
		form: "<",
		title: "@",
		permissions: "<",
		onDetailSelect: "&",
		registerValueProvider: "&",
		getDetail: "&",
		setCallbacks: "&",
		enableAddDetail: "=",
		showAddDetail: "=",
		modifiedDates: "<",
		detailValues: "=",
		editDetail: "<",
		dirtyDetails: "=",
		isSubmitted: "<"
	},
	require: {
		reportScope: "^^reportScope"
	}
}).name;
